import { VFC, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  FormControl,
  Typography,
  TextField,
  IconButton,
  Container,
} from "@mui/material";
import { ResultDialog, ResultResponse } from "../common/dialog/ResultDialog";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosResponse } from "axios";
import { Utility } from "../../utils/Utility";
import Constants from "../../utils/Constants";
import { FormLabel } from "../common/form/FormLabel";
import { AlertDialog } from "../common/dialog/AlertDialog";

import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EditOffOutlinedIcon from '@mui/icons-material/EditOffOutlined';
import CompanyDialogMode from "../../entity/CompanyDialogMode";
import { CompanyCreateFormSchema, CompanyEditForm, CompanyEditFormSchema } from "./api/CompanyForm";
import { CompanyService } from "./api/CompanyService";
import { ProtectedApiAuth } from "../protected/ProtectedApiAuth";

type Props = {
  isAlertDialogOpen: boolean;
  mode: string;
  companyId: string;
  defaultDialogMode: CompanyDialogMode;
  handleSetCompanyId?: Function;
  handleAlertCancel: () => void;
  handleAlertOk: () => void;
};

/**
 * 確認ダイアログ
 * @param param0
 * @returns
 */
const CompanyDialog: VFC<Props> = ({
  isAlertDialogOpen,
  mode,
  companyId,
  defaultDialogMode,
  handleSetCompanyId = () => {},
  handleAlertCancel,
  handleAlertOk,
}) => {

  const CONTENT_WIDTH = 7;
  const LABEL_WIDTH = 5;

  /***************************************************************
   * State管理
   **************************************************************/

  // ダイヤログモード
  const [dialogMode, setDialogMode] = useState<CompanyDialogMode>(
    defaultDialogMode !== null || companyId !== "" ? defaultDialogMode : CompanyDialogMode.CREATE
  );

  // 企業情報
  const [companyEditForm, setCompanyEditForm] = useState(new CompanyEditForm());

  // 新規作成・編集・複製確認ダイアログ
  const [isEditAlertDialogOpen, setIsEditAlertDialogOpen] = useState(false);

  // 削除確認ダイアログ
  const [isDeleteAlertDialogOpen, setIsDeleteAlertDialogOpen] = useState(false);

  // 共通ダイアログ
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    handleCancel();
    handleAlertOk();
    setIsDialogOpen(false);
  };

  // バックエンドからのメッセージを設定
  const setBackendValidMessage = (response: any) => {
    let error = response;
    if (error.companyName) {
      setError("companyName", { message: error.companyName });
    }
    if (error.location) {
      setError("location", { message: error.location });
    }
    if (error.representativeName) {
      setError("representativeName", { message: error.representativeName });
    }
    if (error.companyUrl) {
      setError("companyUrl", { message: error.companyUrl });
    }
  };

  // Formのリセット
  const resetForm = () => {
    reset({
      companyId: "",
      location: "",
      representativeName: "",
      companyUrl: "",
    });
  }

  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    setIsDialogOpen(true);
    setResultResponse({
      status,
      code,
      displayMessage,
      result: [result],
      errorMessages: [],
    });
  };

  // キャンセル処理
  const handleCancel = () => {
    if (dialogMode === CompanyDialogMode.CREATE) {
      resetForm();
    } else {
      setDialogMode(CompanyDialogMode.DETAIL);
    }
    handleAlertCancel();
  }

  /***************************************************************
     * 初期表示処理
     **************************************************************/
  // useEffect(() => {
  //   if (companyId !== "") {
  //     // 企業情報取得
  //     findByEditId();
  //   }
  // }, [companyId]);

  useEffect(() => {
    setDialogMode(defaultDialogMode !== null || companyId !== "" ? defaultDialogMode : CompanyDialogMode.CREATE);
    
    if (companyId !== "" && defaultDialogMode === CompanyDialogMode.DETAIL) {
      // 企業情報取得
      findByEditId();
    }

    if (defaultDialogMode === CompanyDialogMode.CREATE) {
      resetForm();
    }
    
    
  }, [defaultDialogMode, companyId]);

  // テナント情報取得API
  const findByEditId = () => {
    const service = new CompanyService();
    service
      .findByEditId(companyId)
      .then((response: AxiosResponse) => {
        const company = response.data.result;
        setValue("companyId", companyId);
        setValue("companyName", company.companyName);
        setValue("location", company.location);
        setValue("representativeName", company.representativeName);
        setValue("companyUrl", company.companyUrl);
        setValue("createUser", company.createUser);
        setValue("createDate", company.createDate);
        setValue("updateUser", company.updateUser);
        setValue("updateDate", company.updateDate);

        setCompanyEditForm(response.data.result);

      })
      .catch((err) => {})
      .finally(() => {});
  };

  /***************************************************************
   * 新規作成・編集処理
   **************************************************************/
  const handleEditAlertOpen = () => {
    setIsEditAlertDialogOpen(true);
  };
  // 新規作成・編集確認ダイアログ(CLOSE)
  const handleEditAlertClose = () => {
    setIsEditAlertDialogOpen(false);
  };
  // 保存ボタン押下(入力チェック)
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    reset,
    control,
    formState: { errors },
  } = useForm<CompanyEditForm>({
    resolver: yupResolver(
      dialogMode === CompanyDialogMode.EDIT ?  CompanyEditFormSchema : CompanyCreateFormSchema
    ),
    reValidateMode: "onSubmit",
    defaultValues: new CompanyEditForm(),
  });

  // テナント情報新規作成確認API or テナント情報編集確認API
  const handleEditBtn: SubmitHandler<CompanyEditForm> = (hookForm) => {
    const service = new CompanyService();
    switch (dialogMode) {
      case CompanyDialogMode.CREATE:
        // 新規作成
        service
          .createConfirm(hookForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setCompanyEditForm(getValues());
              handleEditAlertOpen();
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
        break;
      case CompanyDialogMode.EDIT:
      // テナント情報編集確認API
        service
          .updateConfirm(hookForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setCompanyEditForm(getValues());
              handleEditAlertOpen();
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
        break;
    }
  };
  // テナント情報新規作成API or テナント情報編集API
  const handleEditConfirmBtn = () => {
    const service = new CompanyService();

    handleEditAlertClose();
    setCompanyEditForm(getValues());

    switch (dialogMode) {
      case CompanyDialogMode.CREATE:
        service
          .create(companyEditForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setIsDialogOpen(true);
              handleSetCompanyId(response.data.result);
              setResultResponse(response.data);
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
        break;
      case CompanyDialogMode.EDIT:
        // テナント情報編集API
        service
          .update(companyEditForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setIsDialogOpen(true);
              setResultResponse(response.data);
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
    }
  };

  /***************************************************************
   * 削除処理
   **************************************************************/
  // 削除確認ダイアログ(OPEN)
  const handleDeleteAlertOpen = () => {
    setIsDeleteAlertDialogOpen(true);
  };
  // 削除確認ダイアログ(CLOSE)
  const handleDeleteAlertClose = () => {
    setIsDeleteAlertDialogOpen(false);
  };
  // 削除ボタン押下
  const handleDeleteBtn = () => {
    const service = new CompanyService();

    if ("" !== companyId) {
      setIsDeleteAlertDialogOpen(false);
      service
        .delete(companyId)
        .then((response) => {
          if ("OK" === response.data.status) {
            setIsDialogOpen(true);
            setResultResponse(response.data);
          } else {
            setIsDialogOpen(true);
            setResultResponse(response.data);
          }
        })
        .catch((err) => {
          setStateResultResponse(
            Constants.ERR_STATUS_NG,
            err.response.status,
            err.response.statusText,
            Constants.E_MSG_SYSTEM_ERR
          );
        })
        .finally(() => {
          // TODO 必要ならローディング
        });
    }
  };

  /***************************************************************
   * 編集処理
   **************************************************************/
  const handleChangeEditMode = () => {
    setDialogMode(dialogMode === CompanyDialogMode.DETAIL ? CompanyDialogMode.EDIT : CompanyDialogMode.DETAIL);
  }

  /***************************************************************
   * 権限処理
   **************************************************************/
  // 権限なしのオーバーライド
  const overrideFunction = (response: ResultResponse) => {
    setResultResponse(response);
    setIsDialogOpen(true);
  }

  /***************************************************************
   * レイアウト
   **************************************************************/

  // ダイヤログ名
  const dialogName = (dialogMode === CompanyDialogMode.CREATE) ? "企業追加" :
                     (dialogMode === CompanyDialogMode.DETAIL) ? "企業詳細" :
                                                                 "企業編集";

  return (
    <>
      <Dialog 
        open={isAlertDialogOpen}
        fullWidth
      >
        <DialogTitle>
          <Grid container justifyContent={"space-between"}>
            <Grid item xs={false}>{dialogName}</Grid>
            {/* ボタン */}
            {(dialogMode !== CompanyDialogMode.CREATE) && <>
              <Grid item xs={false}>
                <Container>
                  <ProtectedApiAuth
                    authcd={Constants.AUTH_COMPANY_DELETE}
                    defaultFunction={handleDeleteAlertOpen}
                    overrideFunction={overrideFunction}
                  >
                    <IconButton>
                      <DeleteOutlinedIcon/>
                    </IconButton>
                  </ProtectedApiAuth>
                  <ProtectedApiAuth
                    authcd={Constants.AUTH_COMPANY_EDIT}
                    defaultFunction={handleChangeEditMode}
                    overrideFunction={overrideFunction}
                  >
                    <IconButton>
                      {dialogMode === CompanyDialogMode.DETAIL ? <>
                        <EditOutlinedIcon/>
                      </> : <>
                        <EditOffOutlinedIcon/>
                      </>}
                    </IconButton>
                  </ProtectedApiAuth>
                </Container>
              </Grid>
            </>}
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Box>
            {/* 入力フォーム */}
            <Box sx={{ p: 4 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  {/* 企業ID */}
                  {dialogMode !== CompanyDialogMode.CREATE &&
                    <>
                      <FormLabel xs={LABEL_WIDTH} label="企業ID" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(companyId)}
                        </Typography>
                      </Grid>
                    </>
                  }
                  {/* 企業名 */}
                  <FormLabel xs={LABEL_WIDTH} label="企業名" isMandatory={dialogMode !== CompanyDialogMode.DETAIL} />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === CompanyDialogMode.DETAIL ? <>
                        <Grid item xs={CONTENT_WIDTH}>
                          <Typography noWrap component="div" align="left">
                            {Utility.NullEmptyStringWrap(companyEditForm.companyName)}
                          </Typography>
                        </Grid>
                      </> : <>
                        <FormControl fullWidth>
                          <TextField
                            fullWidth
                            size="small"
                            {...register("companyName")}
                            error={"companyName" in errors}
                            helperText={errors.companyName?.message}
                          />
                        </FormControl>
                      </>
                    }
                  </Grid>
                  {/* 所在地 */}
                  <FormLabel xs={LABEL_WIDTH} label="所在地" isMandatory={dialogMode !== CompanyDialogMode.DETAIL} />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === CompanyDialogMode.DETAIL ? <>
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(companyEditForm.location)}
                        </Typography>
                      </Grid>
                    </> : <>
                      <FormControl fullWidth>
                        <TextField
                          fullWidth
                          size="small"
                          {...register("location")}
                          error={"location" in errors}
                          helperText={errors.location?.message}
                        />
                      </FormControl>
                    </>}
                  </Grid>
                  {/* 代表者名 */}
                  <FormLabel xs={LABEL_WIDTH} label="代表者名" />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === CompanyDialogMode.DETAIL ? <>
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(companyEditForm.representativeName)}
                        </Typography>
                      </Grid>
                    </> : <>
                      <FormControl fullWidth>
                        <TextField
                          fullWidth
                          size="small"
                          {...register("representativeName")}
                          error={"representativeName" in errors}
                          helperText={errors.representativeName?.message}
                        />
                      </FormControl>
                    </>}
                  </Grid>
                  {/* 企業URL */}
                  <FormLabel xs={LABEL_WIDTH} label="URL" isMandatory={dialogMode !== CompanyDialogMode.DETAIL} />
                  <Grid item xs={CONTENT_WIDTH}>
                    {dialogMode === CompanyDialogMode.DETAIL ? <>
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(companyEditForm.companyUrl)}
                        </Typography>
                      </Grid>
                    </> : <>
                      <FormControl fullWidth>
                        <TextField
                          fullWidth
                          size="small"
                          {...register("companyUrl")}
                          error={"companyUrl" in errors}
                          helperText={errors.companyUrl?.message}
                        />
                      </FormControl>
                    </>}
                  </Grid>
                  {dialogMode !== CompanyDialogMode.CREATE &&
                    <>
                      {/* 作成者 */}
                      <FormLabel xs={LABEL_WIDTH} label="作成者" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(companyEditForm.createUser)}
                        </Typography>
                      </Grid>
                      {/* 作成日時 */}
                      <FormLabel xs={LABEL_WIDTH} label="作成日時" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(companyEditForm.createDate)}
                        </Typography>
                      </Grid>
                      {/* 更新者 */}
                      <FormLabel xs={LABEL_WIDTH} label="更新者" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(companyEditForm.updateUser)}
                        </Typography>
                      </Grid>
                      {/* 更新日時 */}
                      <FormLabel xs={LABEL_WIDTH} label="更新日時" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(companyEditForm.updateDate)}
                        </Typography>
                      </Grid>
                      {/* 削除日時 */}
                      <FormLabel xs={LABEL_WIDTH} label="削除日時" />
                      <Grid item xs={CONTENT_WIDTH}>
                        <Typography noWrap component="div" align="left">
                          {Utility.NullEmptyStringWrap(companyEditForm.deleteDate)}
                        </Typography>
                      </Grid>
                    </>
                  }
                </Grid>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>キャンセル</Button>
          {dialogMode !== CompanyDialogMode.DETAIL && <>
            <Button onClick={handleSubmit(handleEditBtn)}>{mode}</Button>
          </>}
        </DialogActions>
      </Dialog>

      {/* 新規作成・編集確認ダイアログ */}
      <AlertDialog
        isAlertDialogOpen={isEditAlertDialogOpen}
        title="保存確認"
        message="保存しますか？"
        mode="保存"
        handleAlertCancel={handleEditAlertClose}
        handleAlertOk={handleEditConfirmBtn}
      />

      {/* 削除確認ダイアログ */}
      <AlertDialog
        isAlertDialogOpen={isDeleteAlertDialogOpen}
        title="削除確認"
        message="削除しますか？"
        mode="削除"
        handleAlertCancel={handleDeleteAlertClose}
        handleAlertOk={handleDeleteBtn}
      />

      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />
    </>
  );
};

export default CompanyDialog;